import { DOLLAR_TBD } from "api-types/builders/salesConfig";
import type { DocOption, SalesImage } from "building-types/options/optionSet";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import { ensureDefined } from "common/utils/assertOfType";
import { optionPrice } from "doc/options/optionSetTools";
import { isExteriorPaletteOptionSet } from "doc/options/paletteOptionSet";
import { PlanLibraryBadge } from "lib/components/builders/plan-library/PlanLibraryBadge";
import { OptionSetSalesCategoryMessage } from "lib/components/builders/plan-library/PlanLibraryMessages";
import { PlanLibraryOptionValue } from "lib/components/builders/plan-library/PlanLibraryOptionValue";
import { planLibraryActions } from "lib/components/builders/plan-library/planLibraryReducer";
import PlanLibrarySalesImageTable from "lib/components/builders/plan-library/PlanLibrarySalesImageTable";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "lib/components/builders/plan-library/planLibraryStore";
import type { PlanLibraryOptionSet } from "lib/components/builders/plan-library/planLibraryTypes";
import { getTbdPriceLabel, getTbdPriceLabelOmitDollarPrefix } from "lib/components/helpers/getOptionPriceDisplay";
import { notifySalesImageWarning } from "lib/options/notifySalesImageWarning";
import { SALES_IMAGE_UPLOAD_HINT } from "lib/options/salesImageValidation";
import uploadSalesImageToS3 from "lib/options/uploadSalesImageToS3";
import { salesCategorySelectItems } from "lib/state/salesCategory";
import { notifyUploadError } from "lib/utils/notifyUploadError";
import { useState } from "react";
import Button from "ui/buttons/Button";
import Checkbox from "ui/forms/CheckboxOld";
import Input from "ui/forms/Input";
import Select from "ui/forms/Select";
import Textarea from "ui/forms/Textarea";
import useAppContext from "ui/hooks/useAppContext";
import IconUpload from "ui/icons/IconUpload";
import styles from "./PlanLibraryModal.module.scss";
import PlanLibraryModalWrapper from "./PlanLibraryModalWrapper";

export default function PlanLibraryBooleanOptionSetModal() {
  const {
    builder,
    salesCategoryCustomization,
    onHandleDiscarding,
    overridesToDiscard,
    inheritedOptionSet,
    optionSetOverride,
    modalOptionSet,
    planType,
    planId,
  } = usePlanLibrarySelector((state) => {
    const modalOptionSetId = state.modalOptionSet ? state.modalOptionSet.id : "";

    return {
      builder: state.builder,
      salesCategoryCustomization: state.builder.salesConfig?.salesCategoryCustomization,
      onHandleDiscarding: state.onHandleDiscarding,
      overridesToDiscard: state.overridesToDiscard,
      inheritedOptionSet: state.inheritedOptionSets[modalOptionSetId],
      optionSetOverride: state.overrides[modalOptionSetId],
      modalOptionSet: state.modalOptionSet,
      planType: state.plans.planType,
      planId: state.planId,
    };
  });
  const dispatch = usePlanLibraryDispatch();

  const customTbdPriceLabel = builder.salesConfig?.optionsDisplayConfig?.tbdPriceLabel;
  const tbd = getTbdPriceLabelOmitDollarPrefix(customTbdPriceLabel);

  const { setUserNotification } = useAppContext();
  const [optionSet, setOptionSet] = useState(modalOptionSet);
  const [optionNow, setOptionNow] = useState(modalOptionSet?.options.find(({ id }) => id === "true"));
  const [priceText, setPriceText] = useState(optionPrice(optionNow).toString());

  const inheritedOption = inheritedOptionSet.options.find((x) => x.id === "true");
  const optionOverrides = optionSetOverride?.options?.true;

  if (!inheritedOption) {
    return null;
  }

  const inheritedPrice = inheritedOption.priceTbd ? getTbdPriceLabel(customTbdPriceLabel) : `$${inheritedOption.price}`;

  function isOptionValueOverridden(key: keyof DocOption) {
    return optionOverrides?.[key] !== undefined;
  }

  function discardOptionSetOverrideProp(key: keyof Omit<PlanLibraryOptionSet, "options">) {
    if (!modalOptionSet) {
      return;
    }
    dispatch(planLibraryActions.discardOptionSetPropOverride({ oldOptionSet: modalOptionSet, key }));
    setUserNotification({
      message: `Override discarded in ${inheritedOptionSet.name}`,
      messageType: "success",
    });
  }

  function discardOverrideForOptionProp(key: keyof DocOption, oldOptionSet = modalOptionSet) {
    if (!oldOptionSet || !inheritedOption) {
      return;
    }

    dispatch(planLibraryActions.discardOptionPropOverride({ oldOptionSet, optionId: "true", key }));
    setUserNotification({
      message: `Override discarded in ${inheritedOption.name}`,
      messageType: "success",
    });
  }

  function onCommitPriceText() {
    if (!optionNow) {
      return;
    }

    const updatedOption = { ...optionNow };

    let text = priceText.trim().toLowerCase();
    if (text === "") {
      text = "0";
      updatedOption.price = 0;
    } else {
      const newPrice = parseInt(text);
      if (!isNaN(newPrice) && Number.isInteger(newPrice) && newPrice >= 0) {
        updatedOption.price = newPrice;
        text = newPrice.toString();
      } else {
        text = optionPrice(optionNow).toString(); // revert
      }
    }

    if (updatedOption.price !== optionNow.price) {
      setOptionNow(updatedOption);
    }
    if (priceText !== text) {
      setPriceText(text);
    }
  }

  if (!optionNow || !optionSet) {
    return null;
  }

  return (
    <PlanLibraryModalWrapper
      title={overridesToDiscard ? "Discard Upgrade Overrides" : "Edit Upgrade Overrides"}
      inheritedOptionSet={inheritedOptionSet}
      submitEnabled={(() => {
        if (
          (!!optionSet.name && !!optionNow.name) === false ||
          (isExteriorPaletteOptionSet(optionSet) && optionSet.salesCategory !== SalesCategory.DESIGN_PALETTES)
        ) {
          return false;
        }
        return true;
      })()}
      onSubmit={() => {
        if (!modalOptionSet) {
          return;
        }
        if (overridesToDiscard) {
          onHandleDiscarding?.();
        } else {
          const options = optionSet.options.map((x) => (x.id === optionNow.id ? optionNow : x));

          dispatch(
            planLibraryActions.overrideOptionSet({
              newOptionSet: { ...optionSet, options },
              oldOptionSet: modalOptionSet,
            }),
          );
        }
        dispatch(planLibraryActions.setOverridesToDiscard(null));
        dispatch(planLibraryActions.enableOverrideModal(null));
      }}
    >
      <div className={styles.wrapper}>
        <div className={styles.fieldWrapper}>
          <div className={styles.labelWrapper}>
            <PlanLibraryOptionValue
              field="Name"
              isOverridden={optionSetOverride?.name !== undefined}
              inheritedValue={inheritedOptionSet.name}
            >
              <label htmlFor="name">Sales Name</label>
            </PlanLibraryOptionValue>

            <span className={styles.optionSetBadgeWrapper}>
              <PlanLibraryBadge
                isOverridden={optionSetOverride?.name !== undefined}
                onClickBadge={() => {
                  dispatch(
                    planLibraryActions.setOverridesToDiscard({
                      overridesToDiscard: { name: optionSetOverride.name },
                      onHandleDiscarding: () => discardOptionSetOverrideProp("name"),
                    }),
                  );
                }}
                singleField
              />
            </span>
          </div>
          <Input
            value={optionSet.name}
            type="text"
            name="name"
            required
            onInput={(e) => setOptionSet({ ...optionSet, name: e.currentTarget.value })}
            validate={() => {
              if (optionNow.name.length < 1) {
                return "Option value name cannot be empty.";
              } else {
                return null;
              }
            }}
          />
        </div>

        <div className={styles.halfWidth}>
          <div className={styles.fieldWrapper}>
            <div className={styles.labelWrapper}>
              <PlanLibraryOptionValue
                field="Sales Category"
                isOverridden={optionSetOverride?.salesCategory !== undefined}
                inheritedValue={inheritedOptionSet.salesCategory}
              >
                <label htmlFor="salesCategory">Sales Category</label>
              </PlanLibraryOptionValue>

              <span className={styles.optionSetBadgeWrapper}>
                <PlanLibraryBadge
                  isOverridden={optionSetOverride?.salesCategory !== undefined}
                  onClickBadge={() => {
                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: { salesCategory: optionSetOverride.salesCategory },
                        onHandleDiscarding: () => discardOptionSetOverrideProp("salesCategory"),
                      }),
                    );
                  }}
                  singleField
                />
              </span>
            </div>
            <Select
              options={salesCategorySelectItems(salesCategoryCustomization)}
              onChange={(newCategory) => setOptionSet({ ...optionSet, salesCategory: newCategory as string })}
              value={optionSet.salesCategory}
            />
          </div>

          <div className={styles.fieldWrapper}>
            <div className={styles.labelWrapper}>
              <PlanLibraryOptionValue
                field="Base Option"
                isOverridden={optionSetOverride?.defaultValue !== undefined}
                inheritedValue={inheritedOptionSet.defaultValue}
              >
                <label htmlFor="defaultValue">Select Base Option</label>
              </PlanLibraryOptionValue>

              <span className={styles.optionSetBadgeWrapper}>
                <PlanLibraryBadge
                  isOverridden={optionSetOverride?.defaultValue !== undefined}
                  onClickBadge={() => {
                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: { defaultValue: optionSetOverride.defaultValue },
                        onHandleDiscarding: () => discardOptionSetOverrideProp("defaultValue"),
                      }),
                    );
                  }}
                  singleField
                />
              </span>
            </div>
            <Select
              onChange={(newDefault) =>
                setOptionSet({ ...optionSet, defaultValue: ensureDefined(newDefault).toString() })
              }
              options={(optionSet.options ?? []).map(({ name, id }) => ({ label: name, value: id }))}
              value={optionSet.defaultValue}
            />
          </div>
        </div>

        <OptionSetSalesCategoryMessage salesCategory={optionSet.salesCategory} />

        <div className={styles.halfWidth}>
          <div className={styles.fieldWrapper}>
            <div className={styles.labelWrapper}>
              <PlanLibraryOptionValue
                field="Builder Option ID"
                isOverridden={isOptionValueOverridden("builderOptionCode")}
                inheritedValue={inheritedOption.builderOptionCode || "No Builder Option ID"}
              >
                <label htmlFor="builderOptionCode">Builder Option ID</label>
              </PlanLibraryOptionValue>
              <span className={styles.optionSetBadgeWrapper}>
                <PlanLibraryBadge
                  isOverridden={isOptionValueOverridden("builderOptionCode")}
                  onClickBadge={() => {
                    if (!optionOverrides) {
                      return;
                    }

                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: {
                          options: { true: { builderOptionCode: optionOverrides.builderOptionCode } },
                        },
                        onHandleDiscarding: () => discardOverrideForOptionProp("builderOptionCode"),
                      }),
                    );
                  }}
                  singleField
                />
              </span>
            </div>
            <Input
              name="builderOptionCode"
              type="text"
              value={optionNow.builderOptionCode ?? undefined}
              onInput={(e) => setOptionNow({ ...optionNow, builderOptionCode: e.currentTarget.value })}
            />
          </div>

          <div className={styles.fieldWrapper}>
            <div className={styles.labelWrapper}>
              <PlanLibraryOptionValue
                field="Price"
                isOverridden={isOptionValueOverridden("price")}
                inheritedValue={inheritedPrice}
              >
                <label htmlFor="price">Price</label>
              </PlanLibraryOptionValue>
              <span className={styles.optionSetBadgeWrapper}>
                <PlanLibraryBadge
                  isOverridden={isOptionValueOverridden("price")}
                  onClickBadge={() => {
                    if (!optionOverrides) {
                      return;
                    }
                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: { options: { true: { price: optionOverrides.price } } },
                        onHandleDiscarding: () => discardOverrideForOptionProp("price"),
                      }),
                    );
                  }}
                  singleField
                />
              </span>
            </div>
            <Input
              type="text"
              name="price"
              value={priceText}
              placeholder="0"
              onChange={(e) => setPriceText(e.currentTarget.value)}
              onBlur={() => onCommitPriceText()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onCommitPriceText();
                }
              }}
            />
          </div>

          <div className={styles.fieldWrapper}>
            <div className={styles.labelWrapper}>
              <div />
              <span className={styles.optionSetBadgeWrapper}>
                <PlanLibraryBadge
                  isOverridden={isOptionValueOverridden("priceTbd")}
                  onClickBadge={() => {
                    if (!optionOverrides) {
                      return;
                    }
                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: { options: { [optionNow.id]: { priceTbd: optionOverrides.priceTbd } } },
                        onHandleDiscarding: () => discardOverrideForOptionProp("priceTbd"),
                      }),
                    );
                  }}
                  singleField
                />
              </span>
            </div>
            <div className={styles.checkboxWrapper}>
              <Checkbox
                checked={optionNow.priceTbd}
                disabled={!!priceText && priceText !== "0" && priceText !== DOLLAR_TBD}
                label={
                  <PlanLibraryOptionValue
                    field="price is {DOLLAR_TBD}"
                    isOverridden={isOptionValueOverridden("priceTbd")}
                    inheritedValue={inheritedOption.priceTbd ? "Enabled" : "Disabled"}
                  >
                    <div title={`Will display ${DOLLAR_TBD} when price is 0`}>Price is {DOLLAR_TBD}</div>
                  </PlanLibraryOptionValue>
                }
                onChange={() => {
                  const priceTbd = !optionNow.priceTbd;
                  setOptionNow({ ...optionNow, priceTbd });
                  if (priceTbd) {
                    setPriceText(tbd);
                  } else {
                    setPriceText(optionPrice(optionNow).toString());
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.fieldWrapper}>
          <div className={styles.labelWrapper}>
            <PlanLibraryOptionValue
              field="Description"
              isOverridden={isOptionValueOverridden("description")}
              inheritedValue={inheritedOption.description || "No Description"}
            >
              <label htmlFor="description">Description</label>
            </PlanLibraryOptionValue>

            <span className={styles.optionSetBadgeWrapper}>
              <PlanLibraryBadge
                isOverridden={isOptionValueOverridden("description")}
                onClickBadge={() => {
                  if (!optionOverrides) {
                    return;
                  }
                  dispatch(
                    planLibraryActions.setOverridesToDiscard({
                      overridesToDiscard: { options: { true: { description: optionOverrides.description } } },
                      onHandleDiscarding: () => discardOverrideForOptionProp("description"),
                    }),
                  );
                }}
                singleField
              />
            </span>
          </div>
          <Textarea
            value={optionNow.description ?? ""}
            name="description"
            onInput={(e) => setOptionNow({ ...optionNow, description: e.currentTarget.value })}
            maxLength={600}
          />
        </div>

        <div style={{ marginTop: "2.5em" }}>
          <div className={styles.fieldWrapper}>
            <div className={styles.labelWrapper}>
              {inheritedOption.salesImages && optionNow.salesImages && (
                <PlanLibraryOptionValue
                  field="Sales Images"
                  isOverridden={isOptionValueOverridden("salesImages")}
                  inheritedValue={`${inheritedOption.salesImages.length} Sales Images`}
                >
                  <label htmlFor="salesImages">Sales Images ({optionNow.salesImages.length})</label>
                </PlanLibraryOptionValue>
              )}

              <span className={styles.optionSetBadgeWrapper}>
                <PlanLibraryBadge
                  isOverridden={isOptionValueOverridden("salesImages")}
                  onClickBadge={() => {
                    if (!optionOverrides) {
                      return;
                    }
                    dispatch(
                      planLibraryActions.setOverridesToDiscard({
                        overridesToDiscard: { options: { true: { salesImages: optionOverrides.salesImages } } },
                        onHandleDiscarding: () => discardOverrideForOptionProp("salesImages"),
                      }),
                    );
                  }}
                  singleField
                />
              </span>
            </div>
          </div>

          {optionNow.salesImages && optionNow.salesImages.length > 0 && (
            <PlanLibrarySalesImageTable
              salesImages={optionNow.salesImages}
              setSalesImages={(newImages: SalesImage[]) => {
                setOptionNow({ ...optionNow, salesImages: newImages });
              }}
            />
          )}

          <div className={styles.upload}>
            <Button
              onClick={async (event) => {
                if (!optionNow.salesImages) {
                  return;
                }

                try {
                  const results = await uploadSalesImageToS3({
                    planType,
                    planId,
                    optionSetId: optionSet.id,
                    optionId: optionNow.id,
                  });
                  if (!results) {
                    throw new Error("No results returned from upload");
                  }
                  setOptionNow({ ...optionNow, salesImages: [...optionNow.salesImages, results.salesImage] });
                  notifySalesImageWarning(results.salesImage, setUserNotification);
                } catch (e) {
                  notifyUploadError(e, setUserNotification);
                }
                if (event) {
                  event.stopPropagation();
                }
              }}
              btnStyle="secondary"
              btnSize="medium"
            >
              <IconUpload size={18} />
              <span>Upload</span>
            </Button>
            <div className={styles.uploadHint}>{SALES_IMAGE_UPLOAD_HINT}</div>
          </div>
        </div>
      </div>
    </PlanLibraryModalWrapper>
  );
}
